import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { UserCircle, Lock, Save } from 'lucide-react';
import useToast from '../hooks/useToast';

const Profile = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      showError('Passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    try {
      setSaving(true);
      await api.put('/api/update-password', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      showSuccess('Password updated successfully');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <LayoutWrapper title="Profile Settings" subtitle="Manage your account settings">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Profile Info */}
        <Card>
          <CardHeader><CardTitle>Profile Information</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
                <UserCircle className="w-12 h-12 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{user?.full_name}</h2>
                <p className="text-gray-500">@{user?.username}</p>
                <p className="text-sm text-gray-400">{user?.role}</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div><Label>Email</Label><p className="font-medium">{user?.email}</p></div>
              <div><Label>Mobile</Label><p className="font-medium">{user?.mobile}</p></div>
              <div><Label>Status</Label><p className="font-medium text-green-600">{user?.status}</p></div>
              <div><Label>Admin Access</Label><p className="font-medium">{user?.is_admin ? 'Yes' : 'No'}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" />Change Password</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="old_password">Current Password</Label>
                <Input id="old_password" type="password" value={passwordData.old_password} onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})} required />
              </div>
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input id="new_password" type="password" value={passwordData.new_password} onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})} required />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input id="confirm_password" type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})} required />
              </div>
              <Button type="submit" disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? 'Updating...' : 'Update Password'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </LayoutWrapper>
  );
};

export default Profile;